/**
 * Dice roll animation. Each roll (`GameView.lastRoll`) is played once, one die at a time:
 * each die starts flashing random faces `ROLL_DIE_MS` after the one before and keeps flashing; none
 * lands until the last one has started, then they land in order `DIE_LAND_STAGGER_MS` apart, the
 * last after a full `ROLL_DIE_MS` of rolling (`landTimes`). Dice still waiting aren't shown yet. Each die lands where it stays (the server doesn't sort a
 * fresh roll; your hold mat sorts what you hold). Rolls made before mount aren't replayed. A roll
 * the server marks not animated (`RollView.animate`: a bot's, outside a war with a human in it)
 * doesn't flash: its dice just appear one at a time, `BOT_DIE_STAGGER_MS` apart. The server
 * waits the roll time before a bot's next move. Fast Animations
 * shortens each die's roll on this screen (`anim`).
 *
 * Motion dice (Options → Dice Type → Shake/Fling Dice (3D) or Popple (3D), `setMotionDice`; other players' rolls only with Show 3D Enemy Rolls, `motionFor`): the roll
 * plays in the 3D tray (`dice3d/DiceTray.tsx`) instead, always `motionRollMs()` long
 * (`motionQuickMs()` for rolls that aren't animated: bots, ghosts), and the dice on the table stay
 * hidden until it ends. A die that lands badly is thrown again, so the tray can hold a roll open
 * past that (`holdMotionRoll` / `releaseMotionRoll`); everything timed off the roll waits for it
 * (`useRollFx`, `afterRoll`).
 */
import { useEffect, useState } from 'react';
import {
  BOT_DIE_STAGGER_MS,
  DIE_FACES,
  DIE_LAND_STAGGER_MS,
  ROLL_DIE_MS,
  rollAnimMs,
  type PlayerId,
  type RollView,
} from '@kadice/shared';
import { tuning } from './dice3d/tuning';
import { anim } from './speed';

/** How often a rolling die flashes a new face. */
const FLASH_MS = 90;

/** Motion dice: how long a throw plays in the 3D tray, landing included (before Fast Animations; Dice Lab `rollMs`). */
export const motionRollMs = (): number => tuning().rollMs;
/** Motion dice: the same for a roll that isn't animated (a bot's, a ghost roll; Dice Lab `quickMs`). */
export const motionQuickMs = (): number => tuning().quickMs;

let motion = false;
/** Motion dice on for this screen (the option, where WebGL works and motion isn't reduced). */
export const setMotionDice = (on: boolean): void => {
  motion = on;
};
export const motionDice = (): boolean => motion;

let poppleMode = false;
let enemy3d = true;
let viewer: PlayerId | null = null;
/** Popple (tap the bubble to pop-roll) rather than shake/fling, and whether other players' rolls play in 3D (Options). */
export const setMotionMode = (popple: boolean, enemies: boolean): void => {
  poppleMode = popple;
  enemy3d = enemies;
};
export const poppleDice = (): boolean => poppleMode;
/** Who is looking at this screen (their own rolls are always 3D when motion dice are on). */
export const setMotionViewer = (id: PlayerId | null): void => {
  viewer = id;
};
/** Whether `roll` plays in the 3D tray on this screen: motion dice on, and yours or Show 3D Enemy Rolls on. */
export const motionFor = (roll: { playerId?: PlayerId } | null | undefined): boolean =>
  motion && (enemy3d || roll?.playerId === undefined || roll.playerId === viewer);

/** Motion dice: rolls (by `seq`) the tray holds open past their time (a die thrown again), and who waits on each. */
const motionHolds = new Set<number>();
const motionWaiters = new Map<number, Set<() => void>>();
/** The tray keeps roll `seq` going past `rollMs` until `releaseMotionRoll`. */
export const holdMotionRoll = (seq: number): void => {
  motionHolds.add(seq);
};
export const releaseMotionRoll = (seq: number): void => {
  motionHolds.delete(seq);
  const waiting = motionWaiters.get(seq);
  motionWaiters.delete(seq);
  waiting?.forEach((go) => go());
};

/**
 * Run `then` `extraMs` after roll `seq` is over on this screen: `rollPart` ms (its `rollMs`), or
 * later if the 3D tray is holding it open. No roll (`seq` null): just the two times. Returns a cancel.
 */
export function afterRoll(seq: number | null, rollPart: number, extraMs: number, then: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let waiting: (() => void) | null = null;
  const rest = () => {
    waiting = null;
    timer = setTimeout(then, extraMs);
  };
  timer = setTimeout(() => {
    if (seq === null || !motionHolds.has(seq)) return rest();
    waiting = rest;
    let set = motionWaiters.get(seq);
    if (!set) motionWaiters.set(seq, (set = new Set()));
    set.add(rest);
  }, rollPart);
  return () => {
    clearTimeout(timer);
    if (waiting && seq !== null) motionWaiters.get(seq)?.delete(waiting);
  };
}

/** How long this screen animates a roll (`rollAnimMs`, or the 3D throw; shortened by Fast Animations). */
export const rollMs = (roll: { count: number; animate?: boolean; playerId?: PlayerId } | null | undefined): number =>
  anim(
    motionFor(roll) && roll && roll.count > 0
      ? roll.animate !== false
        ? motionRollMs()
        : motionQuickMs()
      : rollAnimMs(roll),
  );

export interface RollFx {
  playerId: PlayerId;
  war: boolean;
  /** One per die rolled, in order: null = landed, 0 = still waiting (not shown), 1-6 = flashing. */
  faces: (number | null)[];
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const randomFace = () => Math.floor(Math.random() * DIE_FACES) + 1;

/**
 * When each die of an animated roll lands (ms from the roll's start; die i starts at i * dieMs).
 * The first lands once the last has started, and late enough that the last rolls a full dieMs.
 */
const landTimes = (count: number, dieMs: number, staggerMs: number): number[] => {
  const first = Math.max((count - 1) * dieMs, count * dieMs - (count - 1) * staggerMs);
  return Array.from({ length: count }, (_, i) => first + i * staggerMs);
};

export function useRollFx(roll: RollView | null): RollFx | null {
  const seq = roll?.seq ?? 0;
  const [state, setState] = useState<{ seen: number; roll: RollView | null; fx: RollFx | null }>({
    seen: seq,
    roll: null,
    fx: null,
  });

  // A new roll: no die shown until the first tick starts it.
  let current = state;
  if (seq > state.seen) {
    const play = roll && roll.count > 0 ? roll : null;
    const fx = play && {
      playerId: play.playerId,
      war: play.war,
      faces: Array<number | null>(play.count).fill(0),
    };
    current = { seen: seq, roll: play, fx };
    setState(current);
  }

  const active = current.roll;
  useEffect(() => {
    if (!active) return;
    const stop = () => setState((s) => (s.roll === active ? { ...s, roll: null, fx: null } : s));
    if (reducedMotion()) return stop();
    // Motion dice: the 3D tray shows the roll; the dice on the table wait, hidden, until it ends.
    if (motionFor(active)) return afterRoll(active.seq, rollMs(active), 0, stop);
    const started = performance.now();
    // Not animated (a bot's): no flashing, each die just appears, BOT_DIE_STAGGER_MS apart.
    const staggered = active.animate === false;
    const dieMs = anim(staggered ? BOT_DIE_STAGGER_MS : ROLL_DIE_MS);
    // Staggered dice land as they start; animated ones keep rolling until `landTimes`.
    const lands = staggered
      ? Array.from({ length: active.count }, (_, i) => i * dieMs)
      : landTimes(active.count, dieMs, anim(DIE_LAND_STAGGER_MS));
    const end = staggered ? active.count * dieMs : lands[active.count - 1]!;
    const tick = () => {
      const elapsed = performance.now() - started;
      if (elapsed >= end) return stop();
      const faces = Array.from({ length: active.count }, (_, i) =>
        elapsed >= lands[i]! ? null : elapsed >= i * dieMs ? randomFace() : 0,
      );
      setState((s) => (s.roll === active && s.fx ? { ...s, fx: { ...s.fx, faces } } : s));
    };
    tick();
    const flash = setInterval(tick, staggered ? 25 : FLASH_MS);
    return () => clearInterval(flash);
  }, [active]);

  return current.fx;
}

/**
 * `values` as shown mid-roll: the rolled dice start at `from`. A die not shown yet is 0. Also
 * the indexes not landed yet (rolling, or waiting); non-empty until the whole roll has landed,
 * and nothing is usable until then.
 */
export function rollingDice(
  values: readonly number[],
  from: number,
  fx: RollFx | null,
): { values: number[]; rolling: number[] } {
  if (!fx) return { values: [...values], rolling: [] };
  const rolling: number[] = [];
  const shown = values.map((v, i) => {
    const k = i - from;
    if (k < 0 || k >= fx.faces.length) return v;
    if (fx.faces[k] !== null) rolling.push(i);
    // Landed (null): its value; waiting: 0 (not shown); rolling: the flashing face.
    return fx.faces[k] ?? v;
  });
  return { values: shown, rolling };
}
